import React, { useMemo, useState } from 'react';
import { CheckCircleIcon } from '../icons';
import { FedexporInitiative, PoweredByLytiks } from '../ui/Logo';
import { isStrongPassword, passwordChecks } from '../../utils/password';

const InitialPasswordChangeGate = ({ currentUser, onComplete, onLogout }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const checks = useMemo(() => passwordChecks(newPassword), [newPassword]);
  const passwordsMatch = Boolean(confirmation) && newPassword === confirmation;
  const canSubmit = isStrongPassword(newPassword) && passwordsMatch && !isSubmitting;

  const requirements = [
    ['length', '10 caracteres o más'],
    ['uppercase', 'Una letra mayúscula'],
    ['lowercase', 'Una letra minúscula'],
    ['number', 'Un número'],
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError('');
    try {
      await onComplete(newPassword);
    } catch (changeError) {
      setError(changeError.message || 'No se pudo actualizar la contraseña. Intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100svh] bg-[#F4F7FB] px-4 py-6 text-slate-900 sm:px-6 lg:flex lg:items-center lg:bg-[#06101f] lg:py-10">
      <div className="mx-auto grid w-full max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_70px_rgba(2,8,23,0.22)] lg:grid-cols-[0.9fr_1.1fr] lg:border-white/10">
        <aside className="flex flex-col justify-between bg-[#071326] p-6 text-white sm:p-8">
          <div>
            <FedexporInitiative compact variant="dark" />
            <p className="mt-10 text-[0.65rem] font-bold uppercase tracking-[0.16em] text-[#76A7E8]">Primer ingreso · Paso 1 de 2</p>
            <h1 className="mt-3 text-2xl font-bold leading-tight">Protege tu cuenta antes de continuar</h1>
            <p className="mt-4 text-sm leading-relaxed text-white/60">
              La clave recibida por correo es temporal. Crea una contraseña personal que no utilices en otros servicios.
            </p>
          </div>
          <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.05] p-4 text-sm text-white/65">
            <p className="font-semibold text-white">{currentUser?.name || 'Empresa participante'}</p>
            <p className="mt-1 break-all text-xs text-white/45">{currentUser?.email || currentUser?.contactEmail}</p>
            <p className="mt-4 text-xs leading-relaxed text-white/45">Después revisarás los términos y la política de privacidad.</p>
          </div>
        </aside>

        <main className="p-5 sm:p-8 lg:p-10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#004AAD]">Nueva contraseña</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-900">Elige una clave segura</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">Tu sesión continuará abierta al guardar el cambio.</p>
            </div>
            <button type="button" onClick={onLogout} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800">
              Salir
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-7 space-y-5">
            <div>
              <label htmlFor="new-password" className="block text-sm font-semibold text-slate-700">Nueva contraseña</label>
              <div className="relative mt-1.5">
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3 pr-20 text-sm text-slate-800 focus:border-[#004AAD] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#004AAD]/10"
                />
                <button type="button" onClick={() => setShowPassword(value => !value)} className="absolute inset-y-0 right-0 px-3 text-xs font-semibold text-[#004AAD]">
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-semibold text-slate-700">Confirmar contraseña</label>
              <input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="new-password"
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3 text-sm text-slate-800 focus:border-[#004AAD] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#004AAD]/10"
              />
              {confirmation && !passwordsMatch && <p className="mt-2 text-xs font-medium text-rose-600">Las contraseñas no coinciden.</p>}
            </div>

            <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4 sm:grid-cols-2">
              {requirements.map(([key, label]) => (
                <p key={key} className={`flex items-center gap-2 text-xs font-medium ${checks[key] ? 'text-emerald-700' : 'text-slate-400'}`}>
                  <span className={`flex h-4 w-4 items-center justify-center rounded-full ${checks[key] ? 'bg-emerald-100' : 'bg-slate-200'}`}>{checks[key] ? '✓' : '·'}</span>
                  {label}
                </p>
              ))}
            </div>

            {error && <p role="alert" className="rounded-xl border border-rose-100 bg-rose-50 p-3 text-sm font-medium text-rose-700">{error}</p>}

            <button
              type="submit"
              disabled={!canSubmit}
              className={`flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white transition-all ${canSubmit ? 'bg-[#004AAD] shadow-sm hover:bg-[#003780]' : 'cursor-not-allowed bg-slate-300'}`}
            >
              <CheckCircleIcon className="h-4 w-4" />
              {isSubmitting ? 'Guardando cambio...' : 'Guardar y continuar'}
            </button>
          </form>

          <PoweredByLytiks className="mt-7 border-t border-slate-100 pt-5" />
        </main>
      </div>
    </div>
  );
};

export default InitialPasswordChangeGate;
